import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { useEffect, useState } from "react";
import { FieldErrorsImpl } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { Button, Checkbox, Input, Card } from "@chef/components";
import {
  BRAND_NAME,
  ORDER_TYPE_IDS,
  PAYMENT_METHODS,
  BRAND_PATHS,
  config,
  BRAND_NAME_FULL,
} from "@chef/constants";
import { useExtendedForm } from "@chef/hooks";
import {
  selectGiftcardAmount,
  selectInputType,
  setGiftcardAmount,
  showNotification,
  useUpdateDirectPaymentSessionMutation,
  useCreateDirectOrderMutation,
} from "@chef/state-management";
import { MasterCardBadge, VisaBadge } from "@chef/icons/other";
import { ContentComponent, ISection } from "@chef/feature-sanity";
import { DeliveryChecker } from "@chef/smart-components";
import { toØre } from "@chef/utils/currency";

import {
  CustomAmountGiftCardPicker,
  PresetGiftCardAmountPicker,
} from "./GiftCardAmountPickers";

import { intl, brand } from "./GiftcardOrderForm.Intl";

interface FormData {
  amount: string;
  sender: string;
  email: string;
  terms: boolean;
}

const MINIMUM_AMOUNT = 250;
const MAXIMUM_AMOUNT = 10000;

const schema = yup.object().shape({
  amount: yup
    .string()
    .required(intl.REQUIRED_FIELD)
    .matches(/^\d*$/, intl.JUST_DIGITS_ALLOWED)
    .test(
      "is-not-empty-digit",
      intl.REQUIRED_FIELD,
      (value) => value?.trim() !== "",
    )
    .test(
      "minimum-range",
      intl.GIFTCARD_MINIMUM_AMOUNT({ MINIMUM_AMOUNT }),
      (value) => {
        if (value && /^0\d+/.test(value)) {
          return false; // Fail the test if it starts with '0' and has more digits
        }

        const parsedValue = parseInt(value || "", 10);

        return !value || isNaN(parsedValue) || parsedValue >= MINIMUM_AMOUNT;
      },
    )
    .test(
      "maximum-range",
      intl.GIFTCARD_MAXIMUM_AMOUNT({ MAXIMUM_AMOUNT }),
      (value) => !value || parseInt(value, 10) <= MAXIMUM_AMOUNT,
    ),
  sender: yup.string().required(intl.REQUIRED_NAME_FIELD),
  email: yup
    .string()
    .required(intl.REQUIRED_EMAIL_FIELD)
    .email(intl.NEEDS_TO_BE_EMAIL),
  terms: yup.boolean().oneOf([true]).required(intl.REQUIRED_FIELD),
});

interface GiftCardOrderFormProps {
  howDoesAGiftcardWorksData?: ISection | null;
}

export const GiftCardOrderForm = ({
  howDoesAGiftcardWorksData,
}: GiftCardOrderFormProps) => {
  const dispatch = useDispatch();

  const [isExpandedDeliveryChecker, setIsExpandedDeliveryChecker] =
    useState(false);

  const [updateDirectPaymentSession] = useUpdateDirectPaymentSessionMutation();
  const [createDirectOrderMutation] = useCreateDirectOrderMutation();

  const today = new Date();
  const isAdvent = today.getMonth() === 11 && today.getDate() <= 24;

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    formState: { isSubmitting, isValid, errors },
  } = useExtendedForm<FormData>({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues: {
      amount: "",
      sender: "",
      email: "",
      terms: false,
    },
  });

  const selectedAmount = useSelector(selectGiftcardAmount);
  const inputType = useSelector(selectInputType);
  useEffect(() => {
    // Synchronize Redux state with react-hook-form
    if (selectedAmount !== undefined) {
      setValue("amount", selectedAmount, { shouldValidate: true });
    }

    // Trigger validation
    trigger();
  }, [selectedAmount, setValue, trigger]);

  const onSubmit = async (values: FormData) => {
    if (values.amount && values.sender && values.email && values.terms) {
      try {
        const data = {
          paymentPartner: "0B25518E-4871-46FE-9494-036D6F64A6D6",
          paymentMethod: PAYMENT_METHODS.CARD,
          deliveryDate: new Date(today.setDate(today.getDate() + 1)),
          company: config.companyId,
          campaign: "1E601F3A-C22B-4EBA-BFB4-4F2BF4322CBF",
          products: [
            {
              variationId: "FBD054A1-4665-4E60-8CFF-ABE0473529E6",
              quantity: 1,
              dynamicPrice: +values.amount,
            },
          ],
          orderType: ORDER_TYPE_IDS.GIFTCARD,
          buyerInfo: {
            firstName: values.sender,
            email: values.email,
          },
          giftCardPDFId:
            (["AMK", "GL"].includes(BRAND_NAME) &&
              (isAdvent
                ? brand.ADVENT_GIFT_CARD_PDF_ID
                : brand.GIFT_CARD_PDF_ID)) ||
            brand.GIFT_CARD_PDF_ID,
        };

        const directOrder = await createDirectOrderMutation(data).unwrap();

        const returnUrl = new URL(
          BRAND_PATHS.GIFTCARD_SUCCESS_PAGE,
          window.location.origin,
        );

        const { redirectUri } = await updateDirectPaymentSession({
          directOrderId: directOrder.id,
          payment: {
            redirectUri: returnUrl.toString(),
          },
          subject: {
            firstName: values.sender,
            email: values.email,
            nin: null,
          },
          basket: {
            items: [
              {
                variationId: "FBD054A1-4665-4E60-8CFF-ABE0473529E6",
                quantity: 1,
                name: `${intl.GIFTCARD_FOR} ${BRAND_NAME_FULL}`,
                productId: "31AF56A4-349B-49C5-9CA7-11321CF3728C",
                productTypeId: "55CCCBD3-2BC0-4B4B-BA4A-37B11B2A0BAB",
                price: toØre(+values.amount),
              },
            ],

            _meta: {
              vat:
                BRAND_NAME === "RN"
                  ? toØre(+values.amount * config.vatRate)
                  : 0,
              total: toØre(+values.amount),
              net: toØre(+values.amount),
              credit: 0,
              discount: 0,
              deliveryFee: 0,
            },
          },
          cancellationUrl: window.location.href,
        }).unwrap();

        if (redirectUri) {
          window.location.href = redirectUri;
        }
      } catch (e) {
        console.error(e);
        showNotification({
          type: "error",
          message: intl.SOMETHING_WENT_WRONG,
        });
      }
    }
  };

  const handleToggleDeliveryChecker = () => {
    setIsExpandedDeliveryChecker(!isExpandedDeliveryChecker);
  };

  const isOnlyTermsError = (errors: Partial<FieldErrorsImpl<FormData>>) => {
    const keys = Object.keys(errors);
    return keys.length === 1 && keys[0] === "terms";
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 lg:flex-row"
      >
        <Card noPadding className="py-4 md:px-4 lg:w-3/5">
          <div className="relative mt-5 ml-3 text-xl lg:ml-5 xl:ml-7">
            <h2 className={errors.amount?.message ? "mb-11" : "mb-11"}>
              <strong>{intl.CHOOSE_AMOUNT}</strong>
            </h2>

            {errors.amount?.message && inputType === "none" && (
              <div className="absolute left-0 flex items-center mb-4 ml-1 text-xs -bottom-10">
                <div className="w-2 h-2 mr-1 rounded-full bg-error" />
                {intl.YOU_MUST_SELECT_AN_AMOUNT}
              </div>
            )}
          </div>

          {brand.PRESET_AMOUNTS.map((presetAmount) => (
            <>
              <hr className="border-grey-2" />
              <PresetGiftCardAmountPicker
                onDeselectClicked={() =>
                  dispatch(setGiftcardAmount({ amount: "", inputType: "none" }))
                }
                onSelectClicked={() => {
                  dispatch(
                    setGiftcardAmount({
                      amount: presetAmount.amount,
                      inputType: "preset",
                    }),
                  );
                }}
                amount={presetAmount.amount}
                description={presetAmount.description}
                isActive={
                  presetAmount.amount === selectedAmount &&
                  inputType === "preset"
                }
              />
            </>
          ))}

          <hr className="border-grey-2" />
          <CustomAmountGiftCardPicker
            onSelectClicked={() =>
              dispatch(setGiftcardAmount({ amount: "", inputType: "custom" }))
            }
            onDeselectClicked={() => {
              dispatch(setGiftcardAmount({ amount: "", inputType: "none" }));
            }}
            description={intl.MINIMUM_AMOUNT}
            isActive={inputType === "custom"}
            Input={
              inputType === "custom" && (
                <Input
                  {...register("amount")}
                  // Needed because we need to sync the amount to redux, if not it won't be remembered when we navigate back to the page
                  onChange={(e) =>
                    dispatch(
                      setGiftcardAmount({
                        amount: e.target.value,
                        inputType: "custom",
                      }),
                    )
                  }
                  label={intl.GIFTCARD_AMOUNT}
                  className="mt-2"
                />
              )
            }
          />

          {howDoesAGiftcardWorksData && (
            <ContentComponent block={howDoesAGiftcardWorksData?.content} />
          )}
        </Card>

        <Card className="lg:px-6 lg:w-2/5">
          <h2 className="mt-5 mb-6 ">
            <strong>{intl.YOUR_INFORMATION}</strong>
          </h2>
          <Input {...register("sender")} id="sender" label={intl.SENDER} />
          <Input
            {...register("email")}
            id="email"
            label={intl.YOUR_EMAIL}
            helperText={""}
            className="my-4"
          />
          <p className="mb-6">
            {intl.CHECK_WHERE_WE_DELIVER}{" "}
            <button
              role="link"
              type="button"
              onClick={handleToggleDeliveryChecker}
              className="underline text-primary"
            >
              {intl.HERE}.
            </button>
          </p>
          {isExpandedDeliveryChecker && (
            <div className="my-4">
              <DeliveryChecker showContactForm={false} showCTA={false} />
            </div>
          )}
          <hr className="border-grey-2" />
          <p className="flex justify-between mt-3 mb-6 font-bold">
            <span>{intl.SUM_TO_PAY}</span>
            <span>{selectedAmount || 0} kr</span>
          </p>
          <Checkbox
            {...register("terms")}
            className={isOnlyTermsError(errors) ? "border-error" : ""}
            id="terms-checkbox"
          >
            {intl.I_ACCEPT_CURRENT}
            <Link
              href={BRAND_PATHS.TOS_PATH}
              className="underline"
              target="_blank"
              passHref
            >
              <strong>{intl.TERMS_AND_CONDITIONS}</strong>
            </Link>
            {intl.FOR_PURCHASE_OF_GIFTCARD}
          </Checkbox>
          {isOnlyTermsError(errors) && (
            <div className="flex items-center mt-2 text-xs">
              <div className="w-3 h-3 mr-1 rounded-full bg-error" />
              {intl.ACCEPT_THE_TERMS}
            </div>
          )}
          <Button
            type="submit"
            id="submit-gift-card-order-button"
            loading={isSubmitting}
            disabled={!isValid}
            className="my-6"
            primary
            full
          >
            {intl.TO_PAYMENT}
          </Button>
          {["AMK", "GL"].includes(BRAND_NAME) && (
            <div className="flex gap-1.5 justify-center">
              <VisaBadge width={50} height={60} />
              <MasterCardBadge width={50} height={60} />
            </div>
          )}
        </Card>
      </form>
    </>
  );
};
